import { useQueryClient } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { lazy, Suspense, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  ehAdmin,
  useArquivosPrazo,
  useDriveStatus,
  useExcluirArquivo,
  useGerarPeticao,
  useHistorico,
  useIniciarProcesso,
  useMe,
  usePecasStatus,
  usePrazoDetalhe,
  useTransicao,
  useTransicoesPermitidas,
  useUploadPeticao,
  useVerificarPecas,
  useMapaConteudo,
  useBlocosConteudo,
  useTesesPropostas,
  useAtualizarArsenal,
  useIniciarGeracao,
  useEditarPrazo,
  useExcluirPrazo,
  usePode,
  useServicosProcesso,
  useSubstituirPdfProcesso,
} from '../hooks';
import { ROTULOS_ETIQUETA, type Etiqueta, type EndpointTransicao, type PrazoDetalhe, type EditarPrazoIn } from '../types';
import { VAR_COR, fmtData, fmtDateTime } from '../utils';
import { ProgressoPipeline } from './ProgressoPipeline';
import { MapaMarkdown, BlocosLista } from './pecaRender';
import { DatajudSection } from './DatajudSection';
import { TimelineExpediente } from './TimelineExpediente';

// Split de refino (mapa/blocos à esquerda + terminal à direita) — lazy.
const RefinoSplit = lazy(() =>
  import('./RefinoSplit').then((m) => ({ default: m.RefinoSplit })),
);

interface Props {
  idPrazo: number;
  onClose: () => void;
}

const MAX_MB = 200;
const EXT_OK = ['.pdf', '.docx', '.doc', '.odt', '.rtf', '.txt', '.png', '.jpg', '.jpeg'];

function fmtTamanho(bytes: number | null): string {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** Retorna className do chip conforme enabled/cor */
function chip(enabled: boolean, color: 'blue' | 'purple' | 'amber' | 'emerald' | 'green' | 'cyan') {
  const base = 'flex items-center gap-1 text-xs px-3 py-1.5 rounded border transition-colors';
  if (!enabled) {
    return `${base} opacity-40 cursor-not-allowed pointer-events-none bg-gray-50 dark:bg-gray-800 text-gray-400 dark:text-gray-500 border-gray-200 dark:border-gray-700`;
  }
  const palette = {
    blue:    'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-900/50 border-blue-200 dark:border-blue-800 cursor-pointer',
    purple:  'bg-purple-50 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 hover:bg-purple-100 dark:hover:bg-purple-900/50 border-purple-200 dark:border-purple-800 cursor-pointer',
    amber:   'bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 hover:bg-amber-100 dark:hover:bg-amber-900/50 border-amber-200 dark:border-amber-800 cursor-pointer',
    emerald: 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-100 dark:hover:bg-emerald-900/50 border-emerald-200 dark:border-emerald-800 cursor-pointer',
    green:   'bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300 hover:bg-green-100 dark:hover:bg-green-900/50 border-green-200 dark:border-green-800 cursor-pointer',
    cyan:    'bg-cyan-50 dark:bg-cyan-900/30 text-cyan-700 dark:text-cyan-300 hover:bg-cyan-100 dark:hover:bg-cyan-900/50 border-cyan-200 dark:border-cyan-800 cursor-pointer',
  };
  return `${base} ${palette[color]}`;
}

/** Card de ação grande (Inteligência & Rascunho) — alvo de toque confortável. */
function acaoCls(enabled: boolean, color: 'blue' | 'purple' | 'amber' | 'emerald') {
  const base =
    'flex flex-col items-center justify-center gap-1 rounded-xl border px-3 py-3.5 min-h-[78px] text-center transition active:scale-[0.98] focus-visible:ring-2 focus-visible:ring-offset-1';
  if (!enabled) {
    return `${base} opacity-40 cursor-not-allowed pointer-events-none bg-slate-50 dark:bg-slate-800/40 text-slate-400 dark:text-slate-500 border-slate-200 dark:border-slate-700`;
  }
  const pal = {
    blue:    'bg-blue-50 dark:bg-blue-900/25 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-800/70 hover:bg-blue-100 dark:hover:bg-blue-900/40 hover:border-blue-300 cursor-pointer',
    purple:  'bg-violet-50 dark:bg-violet-900/25 text-violet-700 dark:text-violet-300 border-violet-200 dark:border-violet-800/70 hover:bg-violet-100 dark:hover:bg-violet-900/40 hover:border-violet-300 cursor-pointer',
    amber:   'bg-amber-50 dark:bg-amber-900/25 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-800/70 hover:bg-amber-100 dark:hover:bg-amber-900/40 hover:border-amber-300 cursor-pointer',
    emerald: 'bg-emerald-50 dark:bg-emerald-900/25 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800/70 hover:bg-emerald-100 dark:hover:bg-emerald-900/40 hover:border-emerald-300 cursor-pointer',
  };
  return `${base} ${pal[color]}`;
}

export function ModalDetalhe({ idPrazo: idInicial, onClose }: Props) {
  // Cards por processo — a seção "Serviços do processo" permite navegar entre
  // os jobs do mesmo card sem fechar o modal (troca o idPrazo interno).
  const [idPrazo, setIdPrazo] = useState(idInicial);
  useEffect(() => setIdPrazo(idInicial), [idInicial]);
  const { data: me } = useMe();
  const admin = ehAdmin(me?.papel);
  const pode = usePode();
  // Refino: capacidade configurável pelo SU no painel Escritórios (checkbox).
  const podeRefinar = pode('item.refino');
  // Colaborador também vê o painel COMPLETO (como admin), mas em modo leitura —
  // sem upload/exclusão no Drive e sem as peças geradas (só o PDF dos autos).
  const podeVerCompleto = admin || me?.papel === 'colaborador';
  const queryClient = useQueryClient();
  const { data: c, isLoading, error } = usePrazoDetalhe(idPrazo);
  const { data: historico } = useHistorico(idPrazo);
  const { data: arquivosLive } = useArquivosPrazo(idPrazo);
  const { data: driveStatus } = useDriveStatus();
  const { data: pecas } = usePecasStatus(idPrazo);
  const { data: tp } = useTransicoesPermitidas(idPrazo);
  const uploadMut = useUploadPeticao(idPrazo);
  const excluirMut = useExcluirArquivo(idPrazo);
  const iniciarProcessoMut = useIniciarProcesso(idPrazo);  // pipeline unificado (Mapa do Processo)
  const iniciarGeracaoMut = useIniciarGeracao(idPrazo);
  const gerarPeticaoMut = useGerarPeticao(idPrazo);
  const verificarMut = useVerificarPecas(idPrazo);
  const transicaoMut = useTransicao(idPrazo);
  const inputRef = useRef<HTMLInputElement>(null);
  const [arrastando, setArrastando] = useState(false);
  const [erroUpload, setErroUpload] = useState<string | null>(null);
  const [uploadOk, setUploadOk] = useState<string | null>(null);
  const [progressoUpload, setProgressoUpload] = useState<number | null>(null);
  const [showTerminal, setShowTerminal] = useState(false);
  const [erroTransicao, setErroTransicao] = useState<string | null>(null);
  const [verMapaOpen, setVerMapaOpen] = useState(false);
  const [verBlocosOpen, setVerBlocosOpen] = useState(false);
  const [verArsenalOpen, setVerArsenalOpen] = useState(false);
  const [ajustando, setAjustando] = useState(false);  // modo de edição do prazo ("Ajustar")
  const excluir = useExcluirPrazo();
  async function handleExcluirPrazo() {
    if (!c) return;
    if (!window.confirm(
      `Excluir ${c.titulo || c.numero_processo}?\nO card sai do quadro (soft-cancel) e o evento do Google Agenda é removido.`,
    )) return;
    try {
      await excluir.mutateAsync(c.id_prazo);
      onClose();  // fecha o painel após excluir
    } catch (err: any) {
      window.alert(err?.status === 403 ? 'Sem permissão para excluir' : (err?.message ?? 'Erro ao excluir o prazo'));
    }
  }
  // Teses propostas pela última "Gerar Petição", pendentes de aprovação p/ o arsenal.
  const { data: tesesData } = useTesesPropostas(idPrazo);
  const tesesPropostas = tesesData?.teses ?? [];
  // Quando o usuário clica "Mapa do processo" e ainda não há mapa, geramos e
  // abrimos o viewer automaticamente assim que o mapa ficar pronto.
  const [aposGerarAbrirMapa, setAposGerarAbrirMapa] = useState(false);

  // (a) Verificação FÍSICA do Drive ao abrir o card — reconcilia os IDs de
  // mapa/blocos/docx com os arquivos que de fato existem. One-shot por abertura
  // (admin apenas — colaborador tem modal enxuto sem essas peças).
  useEffect(() => {
    if (admin) verificarMut.mutate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idPrazo, admin]);

  // (b) Quando a geração TERMINA (status sai de 'gerando' → 'pronto'/'erro'), a
  // lista de arquivos do Drive (painel direito) precisa ser re-buscada — senão os
  // MAPA/BLOCOS/.docx recém-criados só apareciam após um refresh manual.
  const statusAnteriorRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    const atual = pecas?.status;
    const anterior = statusAnteriorRef.current;
    if (anterior === 'gerando' && atual && atual !== 'gerando') {
      verificarMut.mutate();  // reconcilia IDs mapa/blocos/docx com o Drive físico
      queryClient.invalidateQueries({ queryKey: ['arquivos', idPrazo] });
      queryClient.invalidateQueries({ queryKey: ['prazo', idPrazo] });
    }
    statusAnteriorRef.current = atual;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pecas?.status, idPrazo]);

  // Auto-abre o viewer do mapa quando a geração disparada pelo botão concluir.
  useEffect(() => {
    if (aposGerarAbrirMapa && pecas?.mapa_url) {
      setVerMapaOpen(true);
      setAposGerarAbrirMapa(false);
    }
  }, [aposGerarAbrirMapa, pecas?.mapa_url]);

  // Prefere lista "live" ao snapshot do detalhe
  const arquivos = arquivosLive ?? c?.arquivos_drive ?? [];
  // Painel direito mostra o PDF do processo + as PETIÇÕES (.docx versionadas),
  // para visualização/exclusão. Intermediários (MAPA.md, blocos.json,
  // processo.txt, pecas_txt) ficam ocultos — abrem pelos viewers próprios.
  const arquivosVisiveis = arquivos
    .filter((a) => {
      const n = a.nome.toLowerCase();
      // Colaborador vê SÓ o PDF dos autos; admin vê PDF + petições .docx.
      return n.endsWith('.pdf') || (admin && n.endsWith('.docx'));
    })
    // Mais recente → mais antigo (criado_em ISO; sem data vai para o fim).
    .sort((a, b) => {
      const ta = a.criado_em ? Date.parse(a.criado_em) : 0;
      const tb = b.criado_em ? Date.parse(b.criado_em) : 0;
      return tb - ta;
    });

  // Flags para estado das peças — TODAS derivam do PDF FÍSICO na lista live (b/h):
  // excluir md/json não altera o estado; excluir o PDF desabilita tudo.
  const temPdf    = arquivos.some(a => a.nome.toLowerCase().endsWith('.pdf'));
  const temDocx   = !!(pecas?.docx_url);
  // Animação isolada por botão: cada chip só anima quando SUA operação está em andamento
  const estaGerando     = pecas?.status === 'gerando';
  const gerandoProcesso = estaGerando && pecas?.gerando_tipo === 'processo';
  const gerandoPeticao  = estaGerando && pecas?.gerando_tipo === 'peticao';

  async function disparaTransicao(nome: EndpointTransicao) {
    setErroTransicao(null);
    try {
      await transicaoMut.mutateAsync({ nome });
    } catch (err: any) {
      if (err?.status === 403) setErroTransicao('Sem permissão');
      else if (err?.status === 409) setErroTransicao('Conflito — alguém alterou primeiro');
      else setErroTransicao(err?.message ?? 'Erro na transição');
    }
  }

  async function tentarUpload(file: File) {
    setErroUpload(null);
    setUploadOk(null);
    const ext = '.' + file.name.split('.').pop()?.toLowerCase();
    if (!EXT_OK.includes(ext)) {
      setErroUpload(`Extensão ${ext} não aceita. Use: ${EXT_OK.join(', ')}`);
      return;
    }
    if (file.size > MAX_MB * 1024 * 1024) {
      setErroUpload(`Arquivo > ${MAX_MB} MB`);
      return;
    }
    setProgressoUpload(0);
    try {
      // Sobrescrever PDF antigo no Drive se houver
      if (ext === '.pdf') {
        const pdfsAntigos = arquivos.filter(a => a.nome.toLowerCase().endsWith('.pdf'));
        for (const pdf of pdfsAntigos) {
          try {
            await excluirMut.mutateAsync(pdf.id);
          } catch (err) {
            console.warn("Falha ao excluir PDF antigo no Drive antes do upload:", err);
          }
        }
      }

      await uploadMut.mutateAsync({ file, onProgress: setProgressoUpload });
      setUploadOk(file.name);
      setTimeout(() => setUploadOk(null), 4000);

      // PDF anexado. O pipeline (preparo→mapa→blocos) é iniciado pelo CARD do quadro
      // ("▶ Iniciar pipeline"), não mais automaticamente aqui — o painel é informativo.
    } catch (e: any) {
      setErroUpload(e.message ?? 'Falha no upload');
    } finally {
      setProgressoUpload(null);
    }
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-slate-950/60 backdrop-blur-sm flex justify-end"
      /* Clicar FORA do painel (no backdrop) fecha. Não há listener de teclado —
         backspace e outras teclas NÃO fecham. O X segue funcionando também. */
      onClick={onClose}
    >
      <motion.div
        initial={{ x: '100%' }}
        animate={{ x: 0 }}
        exit={{ x: '100%' }}
        transition={{ type: 'spring', damping: 28, stiffness: 240 }}
        className="glass-modal w-full md:w-1/2 max-w-none h-full shadow-2xl flex flex-col text-slate-800 dark:text-slate-100 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex-1 overflow-y-auto p-6 scroll-smooth">
          {isLoading && (
            <div className="flex flex-col items-center justify-center h-full text-slate-400 gap-2">
              <span className="animate-spin text-2xl">⏳</span>
              <span>Carregando dados...</span>
            </div>
          )}
          
          {error && (
            <div className="flex flex-col items-center justify-center h-full text-red-500 dark:text-red-400 gap-2">
              <span className="text-2xl">⚠</span>
              <span>Erro ao carregar detalhes: {String(error)}</span>
            </div>
          )}

          {showTerminal && podeRefinar && createPortal(
            <Suspense fallback={null}>
              <RefinoSplit
                idPrazo={idPrazo}
                numeroProcesso={c?.numero_processo}
                onClose={() => setShowTerminal(false)}
                onFinalizado={() => {
                  // salvar.sh devolveu o rascunho refinado ao Drive + renderizou
                  // o .docx + status='pronto'; reatualiza card, peças, mapa e blocos.
                  queryClient.invalidateQueries({ queryKey: ['pecas', idPrazo] });
                  queryClient.invalidateQueries({ queryKey: ['arquivos', idPrazo] });
                  queryClient.invalidateQueries({ queryKey: ['prazo', idPrazo] });
                  queryClient.invalidateQueries({ queryKey: ['prazos'] });
                  queryClient.invalidateQueries({ queryKey: ['mapa-conteudo', idPrazo] });
                  queryClient.invalidateQueries({ queryKey: ['blocos-conteudo', idPrazo] });
                }}
              />
            </Suspense>,
            document.body,
          )}

          {c && !podeVerCompleto && (
            <div className="space-y-6">
              {/* Header Colaborador */}
              <div className="flex items-start justify-between border-b border-slate-200 dark:border-slate-800/80 pb-4">
                <div>
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold uppercase bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 mb-1 border border-slate-200 dark:border-slate-700">
                    {c.sigla_tribunal}
                  </span>
                  <h3 className="text-xl font-display font-bold tracking-tight text-slate-900 dark:text-white">
                    {c.numero_processo}
                  </h3>
                </div>
                <button 
                  onClick={onClose} 
                  className="p-1 rounded-lg text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 transition"
                  aria-label="Fechar"
                >
                  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Status & Deadline Colaborador */}
              <div className="flex items-center gap-2">
                <span
                  className="px-2.5 py-1 text-xs rounded-lg font-semibold border"
                  style={{
                    background: VAR_COR[c.etiqueta as Etiqueta] + '18',
                    color: VAR_COR[c.etiqueta as Etiqueta],
                    borderColor: VAR_COR[c.etiqueta as Etiqueta] + '40',
                  }}
                >
                  {ROTULOS_ETIQUETA[c.etiqueta as Etiqueta]}
                </span>
                <span className="text-xs text-slate-500 dark:text-slate-400 font-medium">
                  {c.tipo_prazo}
                </span>
              </div>

              {/* Bento Card: Processo em PDF (colaborador vê SÓ o PDF dos autos,
                  não os demais documentos do Drive — petições/mapa/blocos). */}
              <div className="bg-slate-50/50 dark:bg-slate-950/40 border border-slate-200 dark:border-slate-800/80 rounded-xl p-4 shadow-sm backdrop-blur-md">
                <h4 className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-3 flex items-center gap-1.5">
                  <span>📄</span> Processo (PDF)
                </h4>
                {arquivos.filter((a) => a.nome.toLowerCase().endsWith('.pdf')).length > 0 ? (
                  <ul className="space-y-2">
                    {arquivos
                      .filter((a) => a.nome.toLowerCase().endsWith('.pdf'))
                      .map((a) => (
                        <li key={a.id} className="group">
                          <a
                            href={a.link ?? '#'}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center justify-between p-2.5 rounded-lg bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800/80 hover:border-blue-500 dark:hover:border-blue-400 text-blue-700 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 transition duration-150"
                            title={a.nome}
                          >
                            <span className="flex items-center gap-2 truncate">
                              <span>📥</span>
                              <span className="truncate font-medium">{a.nome}</span>
                            </span>
                            <span className="text-[10px] text-slate-400 dark:text-slate-500 font-mono">
                              {fmtTamanho(a.tamanho_bytes)}
                            </span>
                          </a>
                        </li>
                      ))}
                  </ul>
                ) : (
                  <p className="text-xs text-slate-400 dark:text-slate-500 italic py-2 text-center">
                    — PDF do processo ainda não disponível no Drive —
                  </p>
                )}
              </div>

              {/* Protocolar / mover */}
              <div className="border-t border-slate-200 dark:border-slate-800/80 pt-4 space-y-3">
                <div className="flex flex-wrap gap-2">
                  {tp?.transicoes.includes('protocolar') && (
                    <button
                      onClick={() => disparaTransicao('protocolar')}
                      disabled={transicaoMut.isPending}
                      className="flex-1 py-2.5 px-4 text-sm bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white rounded-lg font-semibold shadow-md shadow-emerald-900/10 hover:shadow-emerald-950/20 active:scale-[0.98] transition duration-150 flex items-center justify-center gap-1.5"
                    >
                      {transicaoMut.isPending ? (
                        <>
                          <span className="animate-spin text-sm">⏳</span>
                          <span>Processando...</span>
                        </>
                      ) : (
                        <>
                          <span>✅</span>
                          <span>Protocolar (mover p/ CONCLUÍDOS)</span>
                        </>
                      )}
                    </button>
                  )}
                  {tp?.transicoes.includes('desfazer_protocolo') && (
                    <button
                      onClick={() => disparaTransicao('desfazer-protocolo')}
                      disabled={transicaoMut.isPending}
                      className="flex-1 py-2.5 px-4 text-sm bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 rounded-lg font-medium active:scale-[0.98] border border-slate-250 dark:border-slate-700 transition duration-150 flex items-center justify-center gap-1.5"
                    >
                      {transicaoMut.isPending ? (
                        <>
                          <span className="animate-spin text-sm">⏳</span>
                        </>
                      ) : (
                        <>
                          <span>↶</span>
                          <span>Voltar p/ PRONTO P/ PROTOCOLO</span>
                        </>
                      )}
                    </button>
                  )}
                </div>
                {erroTransicao && (
                  <p className="text-xs text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-950/25 border border-red-200 dark:border-red-900/40 p-2.5 rounded-lg flex items-center gap-1">
                    <span>⚠</span> {erroTransicao}
                  </p>
                )}
              </div>
            </div>
          )}

          {c && podeVerCompleto && (
            <div className="space-y-6">
              {/* Header Admin */}
              <div className="flex items-start justify-between border-b border-slate-200 dark:border-slate-800/80 pb-4">
                <div>
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold uppercase bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 mb-1 border border-slate-200 dark:border-slate-700">
                    {c.sigla_tribunal}
                  </span>
                  <h3 className="text-xl font-display font-bold tracking-tight text-slate-900 dark:text-white">
                    {c.numero_processo}
                  </h3>
                </div>
                <div className="flex items-center gap-1.5">
                  <button
                    onClick={() => setAjustando((v) => !v)}
                    className={`px-2.5 py-1.5 rounded-lg text-xs font-semibold border transition flex items-center gap-1 ${
                      ajustando
                        ? 'bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300 border-amber-300 dark:border-amber-700'
                        : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-700 hover:bg-slate-200 dark:hover:bg-slate-700'
                    }`}
                    title="Editar os campos do prazo (corrigir agendamento errado)"
                  >
                    ✏️ {ajustando ? 'Cancelar' : 'Ajustar'}
                  </button>
                  <button
                    onClick={handleExcluirPrazo}
                    disabled={excluir.isPending}
                    className="px-2.5 py-1.5 rounded-lg text-xs font-semibold border transition flex items-center gap-1 bg-red-50 dark:bg-red-950/30 text-red-600 dark:text-red-400 border-red-200 dark:border-red-900/50 hover:bg-red-100 dark:hover:bg-red-900/40 disabled:opacity-50"
                    title="Excluir o prazo (soft-cancel + remove o evento do Google Agenda)"
                  >
                    {excluir.isPending ? '⏳' : '🗑'} Excluir
                  </button>
                  <button
                    onClick={onClose}
                    className="p-1 rounded-lg text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 transition"
                    aria-label="Fechar"
                  >
                    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>

              {/* Formulário de ajuste (corrigir agendamento) */}
              {ajustando && (
                <FormAjustarPrazo c={c} onClose={() => setAjustando(false)} />
              )}

              {/* Status and SLA tag */}
              <div className="flex flex-wrap items-center gap-2 bg-slate-50/50 dark:bg-slate-950/30 border border-slate-200 dark:border-slate-800/60 rounded-xl p-3">
                <span
                  className="px-2.5 py-1 text-xs rounded-lg font-semibold border"
                  style={{
                    background: VAR_COR[c.etiqueta as Etiqueta] + '18',
                    color: VAR_COR[c.etiqueta as Etiqueta],
                    borderColor: VAR_COR[c.etiqueta as Etiqueta] + '40',
                  }}
                >
                  {ROTULOS_ETIQUETA[c.etiqueta as Etiqueta]}
                </span>
                <span className={`text-xs font-semibold px-2 py-1 rounded-md ${
                  c.dias_ate_vencimento < 0
                    ? 'bg-red-500/10 text-red-600 dark:text-red-400 border border-red-500/20'
                    : c.dias_ate_vencimento === 0
                    ? 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border border-amber-500/20'
                    : 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20'
                }`}>
                  vence {fmtData(c.data_vencimento)}
                  {c.dias_ate_vencimento < 0
                    ? ` (venceu ${Math.abs(c.dias_ate_vencimento)}d)`
                    : c.dias_ate_vencimento === 0
                    ? ' (hoje)'
                    : ` (${c.dias_ate_vencimento}d)`}
                </span>
              </div>

              {/* Bento Grid layout para metadados e triagem */}
              <div className="grid grid-cols-1 gap-4">
                {/* Triagem Bento Card */}
                <div className="bg-slate-50/50 dark:bg-slate-950/40 border border-slate-200 dark:border-slate-800/80 rounded-xl p-4 shadow-sm backdrop-blur-md">
                  <h4 className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-2 flex items-center gap-1.5">
                    <span>⚡</span> Tipo / Triagem
                  </h4>
                  <div className="space-y-2 text-sm text-slate-750 dark:text-slate-300">
                    <p><strong className="text-slate-900 dark:text-slate-200 font-semibold">Tipo:</strong> {c.tipo_prazo}</p>
                    {c.triagem ? (
                      <div className="mt-2 space-y-1.5 border-t border-slate-200/50 dark:border-slate-800/50 pt-2">
                        <p>
                          <strong className="text-slate-900 dark:text-slate-200 font-semibold">Urgência:</strong>{' '}
                          <span className="font-semibold text-orange-600 dark:text-orange-400">
                            {c.triagem.urgencia ?? '—'}
                          </span>
                        </p>
                        {c.triagem.acao_sugerida && (
                          <p><strong className="text-slate-900 dark:text-slate-200 font-semibold">Ação sugerida:</strong> {c.triagem.acao_sugerida}</p>
                        )}
                        {c.triagem.prazo_em_dobro && (
                          <div className="flex items-center gap-1.5 text-red-600 dark:text-red-400 bg-red-500/10 border border-red-500/20 px-2 py-1 rounded-md text-xs font-semibold mt-1">
                            <span>⚠</span> Alerta: prazo em dobro detectado.
                          </div>
                        )}
                        {c.triagem.alertas.length > 0 && (
                          <p className="text-orange-600 dark:text-orange-400 text-xs font-medium">
                            <strong>⚠</strong> {c.triagem.alertas.join(' | ')}
                          </p>
                        )}
                      </div>
                    ) : (
                      <p className="text-xs text-slate-400 dark:text-slate-500 italic mt-1">— sem triagem Haiku registrada —</p>
                    )}
                  </div>
                </div>

                {/* Detalhes do Expediente — linha do tempo da contagem */}
                <TimelineExpediente c={c} />

                {/* Cards por processo — serviços (jobs) do mesmo CNJ + PDF dos autos */}
                {c.processo_id && (
                  <ServicosProcessoSection
                    processoId={c.processo_id}
                    idPrazoAtual={idPrazo}
                    admin={admin}
                    onTrocar={(id) => setIdPrazo(id)}
                  />
                )}

                {/* Publicação Bento Card */}
                <div className="bg-slate-50/50 dark:bg-slate-950/40 border border-slate-200 dark:border-slate-800/80 rounded-xl p-4 shadow-sm backdrop-blur-md">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 flex items-center gap-1.5">
                      <span>📰</span> Publicação (Teor)
                    </h4>
                    {c.link_documento && (
                      <a
                        href={c.link_documento}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[11px] text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-0.5 font-medium"
                      >
                        abrir no PJe ↗
                      </a>
                    )}
                  </div>
                  <div className="bg-slate-100 dark:bg-slate-950 text-slate-750 dark:text-slate-200 p-3 rounded-lg text-xs leading-relaxed max-h-40 overflow-y-auto whitespace-pre-wrap border border-slate-200/60 dark:border-slate-900/60 font-sans">
                    {c.teor_resumo ?? '(sem teor capturado)'}
                  </div>
                </div>

                {/* Drive Bento Card */}
                <div className="bg-slate-50/50 dark:bg-slate-950/40 border border-slate-200 dark:border-slate-800/80 rounded-xl p-4 shadow-sm backdrop-blur-md">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 flex items-center gap-1.5">
                      <span>📂</span> Drive {arquivosVisiveis.length > 0 && <span className="text-[10px] opacity-75">({arquivosVisiveis.length})</span>}
                    </h4>
                    {c.drive_folder_link && (
                      <a
                        href={c.drive_folder_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[11px] text-blue-600 dark:text-blue-400 hover:underline font-medium"
                      >
                        abrir pasta ↗
                      </a>
                    )}
                  </div>

                  {driveStatus && !driveStatus.configurado && (
                    <div className="text-[11px] text-amber-700 dark:text-amber-400 bg-amber-500/10 border border-amber-500/20 rounded-lg px-2.5 py-1.5 mb-3 flex items-center gap-1.5">
                      <span>⚠</span> Drive não configurado: {driveStatus.motivo ?? 'token ausente'}
                    </div>
                  )}

                  {admin && (<>
                  {/* Drop zone — upload/anexar só para admin */}
                  <div
                    onDragOver={(e) => { e.preventDefault(); setArrastando(true); }}
                    onDragLeave={() => setArrastando(false)}
                    onDrop={(e) => {
                      e.preventDefault();
                      setArrastando(false);
                      const f = e.dataTransfer.files?.[0];
                      if (f) tentarUpload(f);
                    }}
                    onClick={() => inputRef.current?.click()}
                    className={`border-2 border-dashed rounded-xl p-4 text-center text-xs cursor-pointer transition-all duration-200 ${
                      arrastando
                        ? 'border-blue-500 bg-blue-500/10 text-blue-600 dark:text-blue-400 scale-[1.01]'
                        : 'border-slate-350 dark:border-slate-700 text-slate-500 dark:text-slate-400 hover:border-blue-400 dark:hover:border-blue-500 hover:bg-slate-100/50 dark:hover:bg-slate-900/30'
                    } ${uploadMut.isPending || driveStatus?.configurado === false ? 'pointer-events-none opacity-60' : ''}`}
                  >
                    <input
                      ref={inputRef}
                      type="file"
                      className="hidden"
                      accept={EXT_OK.join(',')}
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f) tentarUpload(f);
                        if (e.target) e.target.value = '';
                      }}
                    />
                    {(progressoUpload !== null || uploadMut.isPending) ? (() => {
                      const pct = progressoUpload ?? 100;
                      // Em 100% o XHR já entregou os bytes ao backend; o que ainda corre
                      // é a subida ao Drive (PDF grande pode levar ~20s). Deixar isso
                      // explícito evita a sensação de "travou em 100%".
                      const noServidor = pct >= 100;
                      return (
                        <div className="py-1">
                          <div className="flex items-center justify-between text-[11px] text-blue-700 dark:text-blue-300 mb-1">
                            <span className="font-semibold">
                              {noServidor ? '⏳ Enviando ao servidor…' : '📤 Enviando…'}
                            </span>
                            <span className="font-mono tabular-nums font-bold">{pct}%</span>
                          </div>
                          <div className="h-2 w-full rounded-full bg-slate-200 dark:bg-slate-800 overflow-hidden">
                            <div
                              className={`h-full bg-gradient-to-r from-blue-500 to-indigo-500 transition-all duration-150 ${noServidor ? 'animate-pulse' : ''}`}
                              style={{ width: `${pct}%` }}
                            />
                          </div>
                          {noServidor && (
                            <div className="text-[10px] text-slate-400 dark:text-slate-500 mt-1">
                              PDFs grandes podem levar alguns segundos. Não feche o painel.
                            </div>
                          )}
                        </div>
                      );
                    })() : (
                      <>
                        <div className="text-base mb-1">📤</div>
                        <span className="font-medium text-slate-700 dark:text-slate-300">Arrastar arquivo ou clicar para anexar</span>
                        <div className="text-slate-400 dark:text-slate-500 text-[10px] mt-1 font-mono">
                          .pdf .docx .odt .rtf .txt — até {MAX_MB} MB
                        </div>
                      </>
                    )}
                  </div>

                  {erroUpload && (
                    <div className="mt-2 text-[11px] text-red-600 dark:text-red-400 bg-red-500/10 border border-red-500/20 px-2 py-1 rounded-md">
                      ⚠ {erroUpload}
                    </div>
                  )}
                  {uploadOk && (
                    <div className="mt-2 text-[11px] text-emerald-600 dark:text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 px-2 py-1 rounded-md">
                      ✓ {uploadOk} enviado com sucesso!
                    </div>
                  )}
                  </>)}

                  {arquivosVisiveis.length > 0 && (
                    <ul className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {arquivosVisiveis.map((a) => {
                        const ehPeticaoPrincipal = a.id === c.drive_peticao_id;
                        return (
                          <li key={a.id} className="flex items-center justify-between gap-2 text-xs p-2 rounded-lg bg-white dark:bg-slate-900 border border-slate-150 dark:border-slate-800/80 hover:border-slate-300 dark:hover:border-slate-700 transition">
                            <a
                              href={a.link ?? '#'}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex-1 truncate text-slate-700 dark:text-slate-300 hover:text-blue-600 dark:hover:text-blue-400 hover:underline flex items-center gap-1.5"
                              title={a.nome}
                            >
                              <span>{ehPeticaoPrincipal ? '⭐' : '📎'}</span>
                              <span className="truncate font-medium">{a.nome}</span>
                            </a>
                            <span className="text-[10px] text-slate-400 dark:text-slate-500 whitespace-nowrap font-mono">
                              {fmtTamanho(a.tamanho_bytes)}
                            </span>
                            {admin && (
                            <button
                              onClick={() => {
                                if (confirm(`Mover "${a.nome}" para a lixeira do Drive?`)) {
                                  excluirMut.mutate(a.id);
                                }
                              }}
                              disabled={excluirMut.isPending}
                              className="text-slate-400 hover:text-red-650 dark:hover:text-red-400 p-1 hover:bg-slate-100 dark:hover:bg-slate-800 rounded transition"
                              title="Mover para lixeira do Drive"
                            >
                              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </button>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  )}

                  <div className="text-[10px] text-slate-400 dark:text-slate-500 mt-3 font-mono leading-tight bg-slate-100 dark:bg-slate-900/50 p-2 rounded border border-slate-200/50 dark:border-slate-800/40">
                    Caminho: /Prazos TRT1/{new Date(c.data_vencimento).getFullYear()}/{c.numero_processo}/
                  </div>
                </div>

                {/* Historico Bento Card */}
                <div className="bg-slate-50/50 dark:bg-slate-950/40 border border-slate-200 dark:border-slate-800/80 rounded-xl p-4 shadow-sm backdrop-blur-md">
                  <h4 className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-3 flex items-center gap-1.5">
                    <span>⏱</span> Histórico ({historico?.length ?? 0})
                  </h4>
                  {historico && historico.length > 0 ? (
                    <div className="relative border-l border-slate-200 dark:border-slate-800 ml-1.5 pl-4 space-y-3.5 py-1">
                      {historico.map((h) => (
                        <div key={h.id} className="relative text-xs">
                          {/* Dot marker */}
                          <span className="absolute -left-[20.5px] top-1 h-2 w-2 rounded-full border border-white dark:border-slate-900 bg-slate-400 dark:bg-slate-600" />
                          <div className="text-[10px] text-slate-400 dark:text-slate-500 font-mono mb-0.5">
                            {fmtDateTime(h.timestamp)}
                          </div>
                          <div className="text-slate-700 dark:text-slate-300 animate-fade-in">
                            {h.de ? (
                              <span className="font-medium">
                                Transição de <span style={{ color: VAR_COR[h.de] }} className="font-semibold">{h.de}</span> p/ <span style={{ color: VAR_COR[h.para] }} className="font-semibold">{h.para}</span>
                              </span>
                            ) : (
                              <span>
                                Criado no estado <span style={{ color: VAR_COR[h.para] }} className="font-semibold">{h.para}</span>
                              </span>
                            )}
                            {h.usuario_nome && (
                              <span className="text-slate-400 dark:text-slate-500"> ({h.usuario_nome})</span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-xs text-slate-400 dark:text-slate-500 italic py-1 text-center">— Nenhum histórico registrado —</p>
                  )}
                </div>

                {/* Andamentos via DataJud (CNJ) */}
                <DatajudSection idPrazo={idPrazo} />
              </div>

              {/* Technical footer */}
              <div className="border-t border-slate-200 dark:border-slate-800/80 pt-4 flex justify-between text-[10px] text-slate-400 font-mono">
                <div>id_prazo: <code>{c.id_prazo}</code></div>
                <div>origem: <code>API real (VPS)</code></div>
              </div>
            </div>
          )}
        </div>
      </motion.div>
      {verMapaOpen && createPortal(
        <ModalMapaViewer
          idPrazo={idPrazo}
          mapaUrl={pecas?.mapa_url ?? undefined}
          onClose={() => setVerMapaOpen(false)}
        />,
        document.body,
      )}
      {verBlocosOpen && createPortal(
        <ModalBlocosViewer
          idPrazo={idPrazo}
          blocosUrl={pecas?.rascunho_url ?? undefined}
          onClose={() => setVerBlocosOpen(false)}
        />,
        document.body,
      )}
      {verArsenalOpen && createPortal(
        <ModalArsenalViewer
          idPrazo={idPrazo}
          onClose={() => setVerArsenalOpen(false)}
        />,
        document.body,
      )}
    </motion.div>
  );
}

/**
 * Formulário "Ajustar" — edita os campos de agendamento/identificação de um
 * prazo (corrige um prazo agendado errado). Envia só o que mudou (diff) e o
 * backend re-sincroniza o evento no Google Calendar.
 */
function FormAjustarPrazo({ c, onClose }: { c: PrazoDetalhe; onClose: () => void }) {
  const editar = useEditarPrazo(c.id_prazo);
  const [erro, setErro] = useState<string | null>(null);
  const tipoEvOrig: 'vencimento' | 'audiencia' = c.tipo_evento === 'audiencia' ? 'audiencia' : 'vencimento';
  const [f, setF] = useState({
    numero_processo: c.numero_processo ?? '',
    sigla_tribunal: c.sigla_tribunal ?? '',
    tipo_prazo: c.tipo_prazo ?? '',
    data_vencimento: c.data_vencimento ?? '',
    data_inicio: c.data_inicio ?? '',
    dias_uteis: String(c.dias_uteis ?? 0),
    tipo_evento: tipoEvOrig,
    data_evento: c.data_evento ?? '',
    hora_evento: c.hora_evento ?? '08:00',
    titulo: c.titulo ?? '',
    observacao: c.observacao ?? '',
  });

  function up(campo: keyof typeof f, valor: string) {
    setF((p) => ({ ...p, [campo]: valor }));
  }

  async function salvar(e: React.FormEvent) {
    e.preventDefault();
    setErro(null);
    const body: EditarPrazoIn = {};
    if (f.numero_processo.trim() !== (c.numero_processo ?? '')) body.numero_processo = f.numero_processo.trim();
    if ((f.sigla_tribunal || '') !== (c.sigla_tribunal ?? '')) body.sigla_tribunal = f.sigla_tribunal.trim() || null;
    if (f.tipo_prazo.trim() !== (c.tipo_prazo ?? '')) body.tipo_prazo = f.tipo_prazo.trim();
    if (f.data_vencimento !== (c.data_vencimento ?? '')) body.data_vencimento = f.data_vencimento;
    if (f.data_inicio !== (c.data_inicio ?? '')) body.data_inicio = f.data_inicio;
    if (Number(f.dias_uteis) !== (c.dias_uteis ?? 0)) body.dias_uteis = Number(f.dias_uteis);
    if (f.tipo_evento !== tipoEvOrig) body.tipo_evento = f.tipo_evento;
    if ((f.data_evento || '') !== (c.data_evento ?? '')) body.data_evento = f.data_evento || null;
    if ((f.hora_evento || '') !== (c.hora_evento ?? '')) body.hora_evento = f.hora_evento;
    if ((f.titulo || '') !== (c.titulo ?? '')) body.titulo = f.titulo.trim() || null;
    if ((f.observacao || '') !== (c.observacao ?? '')) body.observacao = f.observacao.trim() || null;

    if (Object.keys(body).length === 0) { onClose(); return; }
    if (body.numero_processo === '') { setErro('Número do processo não pode ficar vazio.'); return; }
    if (body.data_vencimento === '') { setErro('Vencimento inválido.'); return; }
    try {
      await editar.mutateAsync(body);
      onClose();
    } catch (err: any) {
      if (err?.status === 403) setErro('Sem permissão para ajustar (apenas admin/gerente).');
      else if (err?.status === 404) setErro('Prazo não encontrado.');
      else setErro(err?.message ?? 'Erro ao salvar.');
    }
  }

  const inp = 'w-full text-sm border border-slate-300 dark:border-slate-600 rounded-lg p-2 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-500';
  const lbl = 'block text-[11px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-1';

  return (
    <form onSubmit={salvar} className="bg-amber-50/60 dark:bg-amber-900/15 border border-amber-200 dark:border-amber-800/60 rounded-xl p-4 space-y-3">
      <div className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-amber-700 dark:text-amber-300">
        <span>✏️</span> Ajustar prazo
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="col-span-2"><label className={lbl}>Nº do processo</label><input className={inp} value={f.numero_processo} onChange={(e) => up('numero_processo', e.target.value)} /></div>
        <div><label className={lbl}>Tribunal</label><input className={inp} value={f.sigla_tribunal} onChange={(e) => up('sigla_tribunal', e.target.value)} placeholder="TRT1" /></div>
        <div><label className={lbl}>Dias úteis</label><input type="number" min={0} className={inp} value={f.dias_uteis} onChange={(e) => up('dias_uteis', e.target.value)} /></div>
        <div className="col-span-2"><label className={lbl}>Tipo de prazo</label><input className={inp} value={f.tipo_prazo} onChange={(e) => up('tipo_prazo', e.target.value)} /></div>
        <div><label className={lbl}>Início da contagem</label><input type="date" className={inp} value={f.data_inicio} onChange={(e) => up('data_inicio', e.target.value)} /></div>
        <div><label className={lbl}>Vencimento (fatal)</label><input type="date" className={inp} value={f.data_vencimento} onChange={(e) => up('data_vencimento', e.target.value)} /></div>
        <div><label className={lbl}>Tipo de evento</label><select className={inp} value={f.tipo_evento} onChange={(e) => up('tipo_evento', e.target.value)}><option value="vencimento">Vencimento</option><option value="audiencia">Audiência</option></select></div>
        <div className="grid grid-cols-2 gap-2">
          <div><label className={lbl}>Data evento</label><input type="date" className={inp} value={f.data_evento} onChange={(e) => up('data_evento', e.target.value)} /></div>
          <div><label className={lbl}>Hora</label><input type="time" className={inp} value={f.hora_evento} onChange={(e) => up('hora_evento', e.target.value)} /></div>
        </div>
        <div className="col-span-2"><label className={lbl}>Título (casos/PI)</label><input className={inp} value={f.titulo} onChange={(e) => up('titulo', e.target.value)} placeholder="rótulo do caso (opcional)" /></div>
        <div className="col-span-2"><label className={lbl}>Observação</label><textarea rows={2} className={inp} value={f.observacao} onChange={(e) => up('observacao', e.target.value)} /></div>
      </div>
      {erro && <p className="text-xs text-red-600 dark:text-red-400">⚠ {erro}</p>}
      <div className="flex items-center justify-between gap-2 pt-1">
        <span className="text-[11px] text-amber-700/80 dark:text-amber-400/80">Mudar a data re-sincroniza o Google Calendar.</span>
        <div className="flex gap-2">
          <button type="button" onClick={onClose} disabled={editar.isPending} className="px-3 py-1.5 text-sm rounded-lg bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-300 disabled:opacity-50">Cancelar</button>
          <button type="submit" disabled={editar.isPending} className="px-4 py-1.5 text-sm rounded-lg bg-amber-600 hover:bg-amber-700 text-white font-semibold disabled:opacity-50">{editar.isPending ? '⏳ Salvando…' : '💾 Salvar'}</button>
        </div>
      </div>
    </form>
  );
}

interface MapaViewerProps {
  idPrazo: number;
  mapaUrl?: string;
  onClose: () => void;
}

function ModalMapaViewer({ idPrazo, mapaUrl, onClose }: MapaViewerProps) {
  const { data, isLoading, error } = useMapaConteudo(idPrazo);

  // Parser leve de Markdown para elementos React estilizados (Premium UX)
  function formatarMarkdown(md: string) {
    if (!md) return null;
    const lines = md.split('\n');
    return lines.map((line, idx) => {
      const trimmed = line.trim();
      
      // H1 Header
      if (trimmed.startsWith('# ')) {
        return (
          <h1 
            key={idx} 
            className="text-xl sm:text-2xl font-display font-bold text-slate-900 dark:text-white mt-6 mb-4 border-b border-slate-200 dark:border-slate-800 pb-2 flex items-center gap-2"
          >
            {trimmed.slice(2)}
          </h1>
        );
      }
      
      // H2 Header
      if (trimmed.startsWith('## ')) {
        return (
          <h2 
            key={idx} 
            className="text-lg sm:text-xl font-display font-bold text-slate-800 dark:text-slate-100 mt-5 mb-3 flex items-center gap-1.5"
          >
            {trimmed.slice(3)}
          </h2>
        );
      }
      
      // H3 Header
      if (trimmed.startsWith('### ')) {
        return (
          <h3 
            key={idx} 
            className="text-base sm:text-lg font-display font-bold text-slate-800 dark:text-slate-200 mt-4 mb-2"
          >
            {trimmed.slice(4)}
          </h3>
        );
      }
      
      // List item
      if (trimmed.startsWith('- ') || trimmed.startsWith('* ')) {
        const itemText = trimmed.slice(2);
        return (
          <li key={idx} className="ml-5 list-disc pl-1 text-xs sm:text-sm text-slate-700 dark:text-slate-350 my-1 font-sans">
            {renderizarInline(itemText)}
          </li>
        );
      }
      
      // Blockquote
      if (trimmed.startsWith('> ')) {
        return (
          <blockquote 
            key={idx} 
            className="border-l-4 border-blue-500 bg-blue-500/5 dark:bg-blue-500/10 pl-3 py-2 my-4 text-xs sm:text-sm text-slate-700 dark:text-slate-350 rounded-r-lg font-sans"
          >
            {renderizarInline(trimmed.slice(2))}
          </blockquote>
        );
      }
      
      // Blank space
      if (trimmed === '') {
        return <div key={idx} className="h-2" />;
      }
      
      // Paragraph
      return (
        <p key={idx} className="text-xs sm:text-sm leading-relaxed text-slate-750 dark:text-slate-300 my-2 font-sans">
          {renderizarInline(trimmed)}
        </p>
      );
    });
  }

  // Substitui **texto** por <strong>texto</strong> de forma segura em React
  function renderizarInline(text: string) {
    const parts = text.split('**');
    return parts.map((part, i) => {
      if (i % 2 === 1) {
        return <strong key={i} className="font-semibold text-slate-950 dark:text-white">{part}</strong>;
      }
      return part;
    });
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[60] bg-slate-950/70 backdrop-blur-sm flex justify-start"
      /* fecha SÓ pelo X (canto sup. direito) — backdrop não fecha */
    >
      <motion.div
        initial={{ x: -24, opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        exit={{ x: -24, opacity: 0 }}
        transition={{ type: 'spring', damping: 26, stiffness: 220 }}
        className="w-full md:w-1/2 h-full shadow-2xl flex flex-col bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100 overflow-hidden border-r border-slate-200 dark:border-slate-800"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 dark:border-slate-800/80 bg-slate-50/50 dark:bg-slate-950/40">
          <div className="flex items-center gap-2">
            <span className="text-lg">📋</span>
            <span className="font-display font-bold text-sm sm:text-base tracking-tight text-slate-900 dark:text-white">
              Mapa do caso
            </span>
          </div>
          <div className="flex items-center gap-2">
            {mapaUrl && (
              <a
                href={mapaUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs px-2.5 py-1.5 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-lg text-slate-600 dark:text-slate-300 font-medium transition flex items-center gap-1 border border-slate-200 dark:border-slate-700"
              >
                Abrir no Drive ↗
              </a>
            )}
            <button
              onClick={onClose}
              className="text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 text-2xl font-light leading-none p-1 transition"
              aria-label="Fechar"
            >
              &times;
            </button>
          </div>
        </div>

        {/* Content Area */}
        <div className="flex-1 overflow-y-auto p-6 sm:p-8 bg-white dark:bg-slate-900">
          {isLoading && (
            <div className="flex flex-col items-center justify-center h-full text-slate-400 gap-2">
              <span className="animate-spin text-2xl">⏳</span>
              <span className="font-mono text-xs">Carregando mapa...</span>
            </div>
          )}

          {error && (
            <div className="flex flex-col items-center justify-center h-full text-red-500 gap-2">
              <span className="text-2xl">⚠</span>
              <span className="font-mono text-xs">Erro ao carregar o mapa do processo: {String(error)}</span>
            </div>
          )}

          {data && <MapaMarkdown conteudo={data.conteudo} />}
        </div>
      </motion.div>
    </motion.div>
  );
}

interface BlocosViewerProps {
  idPrazo: number;
  blocosUrl?: string;
  onClose: () => void;
}

// Paleta de cor do badge por tipo de bloco (tipos do RASCUNHO_BLOCOS.json).
const COR_BLOCO: Record<string, string> = {
  ENDERECO:   'bg-slate-500/10 text-slate-600 dark:text-slate-300 border-slate-500/20',
  H1:         'bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20',
  H2:         'bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 border-indigo-500/20',
  TOPICO:     'bg-violet-500/10 text-violet-600 dark:text-violet-400 border-violet-500/20',
  P:          'bg-slate-500/10 text-slate-500 dark:text-slate-400 border-slate-500/20',
  CITACAO:    'bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20',
  PEDIDO:     'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20',
  ASSINATURA: 'bg-pink-500/10 text-pink-600 dark:text-pink-400 border-pink-500/20',
  BLANK:      'bg-slate-400/10 text-slate-400 border-slate-400/20',
  PAGE_BREAK: 'bg-slate-400/10 text-slate-400 border-slate-400/20',
};

/**
 * Visualizador amigável dos blocos tipados do RASCUNHO_BLOCOS.json (req 4).
 * Cada bloco vira um cartão com badge do tipo + o texto, estilizado conforme o
 * tipo (títulos maiores, citação recuada/itálico, pedido destacado). Toggle "raw"
 * mostra o JSON cru para quem quiser conferir a estrutura.
 */
function ModalBlocosViewer({ idPrazo, blocosUrl, onClose }: BlocosViewerProps) {
  const { data, isLoading, error } = useBlocosConteudo(idPrazo);
  const [verRaw, setVerRaw] = useState(false);

  const blocos: any[] = Array.isArray(data?.blocos) ? (data!.blocos as any[]) : [];

  function tipoDoBloco(b: any): string {
    if (b && typeof b === 'object') return String(b.tipo ?? b.type ?? b.tag ?? 'P').toUpperCase();
    return 'P';
  }
  function textoDoBloco(b: any): string {
    if (b == null) return '';
    if (typeof b === 'string') return b;
    return String(b.texto ?? b.conteudo ?? b.text ?? b.valor ?? '');
  }
  function classeTexto(tipo: string): string {
    if (tipo === 'H1') return 'text-base sm:text-lg font-display font-bold text-slate-900 dark:text-white';
    if (tipo === 'H2' || tipo === 'TOPICO') return 'text-sm sm:text-base font-semibold text-slate-800 dark:text-slate-100';
    if (tipo === 'CITACAO') return 'text-xs sm:text-sm italic text-slate-600 dark:text-slate-350 border-l-2 border-amber-400/50 pl-3';
    if (tipo === 'PEDIDO') return 'text-xs sm:text-sm text-slate-800 dark:text-slate-200 font-medium';
    if (tipo === 'ENDERECO' || tipo === 'ASSINATURA') return 'text-xs text-slate-600 dark:text-slate-350 font-mono whitespace-pre-wrap';
    return 'text-xs sm:text-sm text-slate-700 dark:text-slate-300';
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[60] bg-slate-950/70 backdrop-blur-sm flex justify-start"
      /* fecha SÓ pelo X (canto sup. direito) — backdrop não fecha */
    >
      <motion.div
        initial={{ x: -24, opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        exit={{ x: -24, opacity: 0 }}
        transition={{ type: 'spring', damping: 26, stiffness: 220 }}
        className="w-full md:w-1/2 h-full shadow-2xl flex flex-col bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100 overflow-hidden border-r border-slate-200 dark:border-slate-800"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 dark:border-slate-800/80 bg-slate-50/50 dark:bg-slate-950/40">
          <div className="flex items-center gap-2">
            <span className="text-lg">📦</span>
            <span className="font-display font-bold text-sm sm:text-base tracking-tight text-slate-900 dark:text-white">
              Estrutura da petição
            </span>
            {data?.versao != null && (
              <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-violet-500/10 text-violet-600 dark:text-violet-400 border border-violet-500/20">
                v{data.versao}
              </span>
            )}
            {blocos.length > 0 && (
              <span className="text-[10px] text-slate-400 font-mono">{blocos.length} blocos</span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setVerRaw((v) => !v)}
              className="text-xs px-2.5 py-1.5 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-lg text-slate-600 dark:text-slate-300 font-medium transition border border-slate-200 dark:border-slate-700"
              title="Alternar entre visualização amigável e JSON cru"
            >
              {verRaw ? 'Ver amigável' : 'Ver JSON'}
            </button>
            {blocosUrl && (
              <a
                href={blocosUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs px-2.5 py-1.5 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-lg text-slate-600 dark:text-slate-300 font-medium transition flex items-center gap-1 border border-slate-200 dark:border-slate-700"
              >
                Abrir no Drive ↗
              </a>
            )}
            <button
              onClick={onClose}
              className="text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 text-2xl font-light leading-none p-1 transition"
              aria-label="Fechar"
            >
              &times;
            </button>
          </div>
        </div>

        {/* Content Area */}
        <div className="flex-1 overflow-y-auto p-6 sm:p-8 bg-white dark:bg-slate-900">
          {isLoading && (
            <div className="flex flex-col items-center justify-center h-full text-slate-400 gap-2">
              <span className="animate-spin text-2xl">⏳</span>
              <span className="font-mono text-xs">Carregando blocos...</span>
            </div>
          )}

          {error && (
            <div className="flex flex-col items-center justify-center h-full text-red-500 gap-2">
              <span className="text-2xl">⚠</span>
              <span className="font-mono text-xs">Erro ao carregar os blocos: {String(error)}</span>
            </div>
          )}

          {data && verRaw && (
            <pre className="max-w-3xl mx-auto text-[11px] leading-relaxed text-slate-700 dark:text-slate-300 bg-slate-100 dark:bg-slate-950 p-4 rounded-lg border border-slate-200 dark:border-slate-800 overflow-x-auto whitespace-pre-wrap font-mono">
              {JSON.stringify(blocos, null, 2)}
            </pre>
          )}

          {data && !verRaw && <BlocosLista blocos={blocos} />}
        </div>
      </motion.div>
    </motion.div>
  );
}

interface ArsenalViewerProps {
  idPrazo: number;
  onClose: () => void;
}

/**
 * Painel "Atualizar Arsenal" (req 6 / item 3): mostra as teses propostas pela
 * última "Gerar Petição" com opções sim/não/editar, e mescla as aprovadas no
 * arsenal.md canônico (add_tese.py --commit --push) via /atualizar-arsenal.
 */
function ModalArsenalViewer({ idPrazo, onClose }: ArsenalViewerProps) {
  const { data, isLoading } = useTesesPropostas(idPrazo);
  const mut = useAtualizarArsenal(idPrazo);
  const [itens, setItens] = useState<any[]>([]);
  const [resumo, setResumo] = useState<{ adicionadas: number; total: number; push_ok: boolean } | null>(null);

  const _lines = (v: any): string => (Array.isArray(v) ? v.join('\n') : (v ?? ''));

  useEffect(() => {
    const teses = data?.teses ?? [];
    setItens(teses.map((t: any) => ({
      incluir: true,
      tema: t.tema ?? '',
      tese_cdrj: t.tese_cdrj ?? '',
      fundamentos: _lines(t.fundamentos),
      refutacao: _lines(t.refutacao),
      precedentes: _lines(t.precedentes),
      processos_origem: t.processos_origem ?? String(idPrazo),
      nota_revisao: t.nota_revisao ?? `origem: petição prazo #${idPrazo}`,
    })));
  }, [data, idPrazo]);

  function set(i: number, campo: string, valor: any) {
    setItens((prev) => prev.map((it, idx) => (idx === i ? { ...it, [campo]: valor } : it)));
  }

  function split(s: string): string[] {
    return s.split('\n').map((x) => x.trim()).filter(Boolean);
  }

  async function mesclar() {
    const aprovadas = itens
      .filter((it) => it.incluir)
      .map((it) => ({
        tema: it.tema.trim(),
        tese_cdrj: it.tese_cdrj.trim(),
        fundamentos: split(it.fundamentos),
        refutacao: split(it.refutacao),
        precedentes: split(it.precedentes),
        processos_origem: it.processos_origem,
        nota_revisao: it.nota_revisao,
      }))
      .filter((t) => t.tema && t.refutacao.length > 0);
    if (aprovadas.length === 0) return;
    try {
      const r = await mut.mutateAsync(aprovadas);
      setResumo({ adicionadas: r.adicionadas, total: r.total, push_ok: r.push_ok });
    } catch {
      setResumo(null);
    }
  }

  const nIncluir = itens.filter((it) => it.incluir).length;
  const campoCls =
    'w-full text-sm leading-relaxed bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded-md p-2.5 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-violet-500 resize-y';

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[60] bg-slate-950/80 backdrop-blur-md flex items-center justify-center p-4 sm:p-6"
      /* fecha SÓ pelo X (canto sup. direito) — backdrop não fecha */
    >
      <motion.div
        initial={{ scale: 0.95, y: 15 }}
        animate={{ scale: 1, y: 0 }}
        exit={{ scale: 0.95, y: 15 }}
        transition={{ type: 'spring', damping: 25, stiffness: 200 }}
        className="glass-panel w-full max-w-4xl h-[90vh] shadow-2xl flex flex-col text-slate-800 dark:text-slate-100 overflow-hidden border border-slate-200/50 dark:border-slate-800/80"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 dark:border-slate-800/80 bg-slate-50/50 dark:bg-slate-950/40">
          <div className="flex items-center gap-2">
            <span className="text-lg">📚</span>
            <span className="font-display font-bold text-sm sm:text-base text-slate-900 dark:text-white">
              Atualizar Arsenal — teses propostas
            </span>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 text-2xl font-light leading-none p-1"
            aria-label="Fechar"
          >
            &times;
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 sm:p-6 bg-white dark:bg-slate-900 space-y-4">
          {isLoading && (
            <div className="flex flex-col items-center justify-center h-full text-slate-400 gap-2">
              <span className="animate-spin text-2xl">⏳</span>
            </div>
          )}

          {resumo && (
            <div className="text-sm rounded-lg border p-3 bg-emerald-500/10 border-emerald-500/30 text-emerald-700 dark:text-emerald-300">
              ✓ {resumo.adicionadas} de {resumo.total} tese(s) mesclada(s) no arsenal.md.
              {!resumo.push_ok && (
                <div className="mt-1 text-amber-700 dark:text-amber-400 text-xs">
                  ⚠ Gravado localmente, mas o <strong>push</strong> ao GitHub falhou (configure o token em <code>/root/.git-credentials</code> no VPS).
                </div>
              )}
              <button onClick={onClose} className="mt-2 text-xs underline hover:no-underline">Fechar</button>
            </div>
          )}

          {!resumo && !isLoading && itens.length === 0 && (
            <p className="text-center text-slate-400 text-xs italic py-8">— sem teses propostas —</p>
          )}

          {!resumo && itens.map((it, i) => (
            <div
              key={i}
              className={`rounded-xl border p-3 space-y-2 ${
                it.incluir
                  ? 'border-violet-300 dark:border-violet-700 bg-violet-50/40 dark:bg-violet-900/15'
                  : 'border-slate-200 dark:border-slate-800 bg-slate-100/40 dark:bg-slate-900/20 opacity-60'
              }`}
            >
              <div className="flex items-center justify-between gap-2">
                <input
                  value={it.tema}
                  onChange={(e) => set(i, 'tema', e.target.value)}
                  placeholder="Tema da tese"
                  className={campoCls + ' font-semibold'}
                />
                <label className="flex items-center gap-1 text-[11px] text-slate-600 dark:text-slate-300 shrink-0 cursor-pointer">
                  <input type="checkbox" checked={it.incluir} onChange={(e) => set(i, 'incluir', e.target.checked)} />
                  incluir
                </label>
              </div>
              {it.incluir && (
                <>
                  <div>
                    <label className="text-[10px] uppercase tracking-wide text-slate-400">Tese CDRJ</label>
                    <textarea rows={4} value={it.tese_cdrj} onChange={(e) => set(i, 'tese_cdrj', e.target.value)} className={campoCls} />
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-wide text-slate-400">Fundamentos (1 por linha)</label>
                    <textarea rows={4} value={it.fundamentos} onChange={(e) => set(i, 'fundamentos', e.target.value)} className={campoCls} />
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-wide text-amber-600 dark:text-amber-400">Refutação / antítese (obrigatória, 1 por linha)</label>
                    <textarea rows={4} value={it.refutacao} onChange={(e) => set(i, 'refutacao', e.target.value)} className={campoCls} />
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-wide text-slate-400">Precedentes (1 por linha)</label>
                    <textarea rows={4} value={it.precedentes} onChange={(e) => set(i, 'precedentes', e.target.value)} className={campoCls} />
                  </div>
                </>
              )}
            </div>
          ))}
        </div>

        {!resumo && itens.length > 0 && (
          <div className="px-5 py-3 border-t border-slate-200 dark:border-slate-800/80 bg-slate-50/50 dark:bg-slate-950/40 flex items-center justify-between gap-3">
            <span className="text-xs text-slate-500 dark:text-slate-400">
              {nIncluir} de {itens.length} marcada(s) para incluir
            </span>
            <div className="flex items-center gap-2">
              <button
                onClick={onClose}
                disabled={mut.isPending}
                className="py-2 px-4 text-sm bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 border border-slate-300 dark:border-slate-700 rounded-lg font-medium disabled:opacity-50"
                title="Fechar sem mesclar nada no arsenal"
              >
                ✕ Não mesclar
              </button>
              <button
                onClick={mesclar}
                disabled={mut.isPending || nIncluir === 0}
                className="py-2 px-4 text-sm bg-violet-600 hover:bg-violet-700 disabled:opacity-50 text-white rounded-lg font-semibold flex items-center gap-1.5"
                title="Mesclar as teses marcadas no arsenal.md (commit + push)"
              >
                {mut.isPending ? <><span className="animate-spin">⏳</span> Mesclando…</> : <>📚 Mesclar no arsenal</>}
              </button>
            </div>
          </div>
        )}
      </motion.div>
    </motion.div>
  );
}

/** Cards por processo — lista os SERVIÇOS (jobs) do mesmo CNJ, permite navegar
 *  entre eles e substituir o PDF dos autos (troca só o PDF; os artefatos por
 *  job — split/blocos/docx — ficam intactos nas suas pastas). */
function ServicosProcessoSection({
  processoId,
  idPrazoAtual,
  admin,
  onTrocar,
}: {
  processoId: number;
  idPrazoAtual: number;
  admin: boolean;
  onTrocar: (id: number) => void;
}) {
  const { data: servicos } = useServicosProcesso(processoId);
  const subMut = useSubstituirPdfProcesso(processoId);
  const [pct, setPct] = useState(0);
  const [msgPdf, setMsgPdf] = useState<string | null>(null);
  const pdfRef = useRef<HTMLInputElement>(null);

  async function trocarPdf(f: File) {
    setMsgPdf(null);
    setPct(0);
    try {
      const r = await subMut.mutateAsync({ arquivo: f, onProgress: setPct });
      setMsgPdf(`✓ Autos substituídos (${r.pdf_nome ?? f.name}) — os novos serviços passam a ler o PDF atualizado.`);
    } catch (err: any) {
      if (err?.status === 409) setMsgPdf('⚠ Há um preparo/geração em andamento neste processo — aguarde concluir para trocar o PDF.');
      else if (err?.status === 413) setMsgPdf('⚠ PDF muito grande (limite 500 MB).');
      else setMsgPdf(`⚠ ${err?.message ?? 'Falha ao substituir o PDF'}`);
    }
  }

  if (!servicos || servicos.length === 0) return null;

  return (
    <div className="bg-slate-50/50 dark:bg-slate-950/40 border border-slate-200 dark:border-slate-800/80 rounded-xl p-4 shadow-sm backdrop-blur-md">
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 flex items-center gap-1.5">
          <span>🗂</span> Serviços do processo <span className="text-[10px] opacity-75">({servicos.length})</span>
        </h4>
        {admin && (
          <>
            <input
              ref={pdfRef}
              type="file"
              accept="application/pdf"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) void trocarPdf(f);
                e.target.value = '';
              }}
            />
            <button
              onClick={() => pdfRef.current?.click()}
              disabled={subMut.isPending}
              className="text-[11px] text-blue-600 dark:text-blue-400 hover:underline font-medium disabled:opacity-50"
              title="Envia os autos atualizados: troca SÓ o PDF do processo — as peças já geradas nos serviços ficam intactas"
            >
              {subMut.isPending ? `⏳ enviando… ${pct}%` : '🔄 Substituir PDF dos autos'}
            </button>
          </>
        )}
      </div>
      {msgPdf && (
        <p className={`text-[11px] mb-2 ${msgPdf.startsWith('✓') ? 'text-green-600 dark:text-green-400' : 'text-amber-700 dark:text-amber-400'}`}>
          {msgPdf}
        </p>
      )}
      <div className="space-y-1.5">
        {servicos.map((s) => {
          const atual = s.id_prazo === idPrazoAtual;
          return (
            <button
              key={s.id_prazo}
              onClick={() => !atual && onTrocar(s.id_prazo)}
              className={`w-full text-left rounded-lg border px-3 py-2 text-xs transition flex items-center gap-2 ${
                atual
                  ? 'border-blue-400 dark:border-blue-600 bg-blue-50/60 dark:bg-blue-900/20 cursor-default'
                  : 'border-slate-200 dark:border-slate-800 bg-white/60 dark:bg-slate-900/40 hover:border-blue-300 dark:hover:border-blue-700 cursor-pointer'
              }`}
              title={atual ? 'Serviço aberto neste painel' : 'Abrir este serviço no painel'}
            >
              <span className="font-semibold text-slate-700 dark:text-slate-200 truncate">
                {s.tipo_prazo}
              </span>
              <span className="text-slate-400 dark:text-slate-500 shrink-0">
                venc. {fmtData(s.data_vencimento)}
              </span>
              <span className="ml-auto shrink-0 text-[10px] px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400">
                {ROTULOS_ETIQUETA[s.etiqueta as Etiqueta] ?? s.etiqueta}
              </span>
              {s.etapa_pipeline && (
                <span className="shrink-0 text-[10px] text-violet-600 dark:text-violet-400" title="Etapa do pipeline">
                  {s.etapa_pipeline}
                </span>
              )}
              {s.tem_peticao_drive && <span className="shrink-0" title="Autos/peça no Drive">📎</span>}
              {atual && <span className="shrink-0 text-[10px] text-blue-600 dark:text-blue-400 font-bold">← este</span>}
            </button>
          );
        })}
      </div>
    </div>
  );
}
